import React, { useState, useEffect } from "react";
import { useI18n } from "./i18n.js";

export default function App() {
  const { lang, setLang, t } = useI18n();

  const [zoomImage, setZoomImage] = useState(null);
  const closeZoom = () => setZoomImage(null);
  // Плавный скролл с учётом фикс-хедера
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const el = document.querySelector(a.getAttribute("href"));
      if (!el) return;
      e.preventDefault();
      const y = el.getBoundingClientRect().top + window.pageYOffset - 88;
      window.scrollTo({ top: y, behavior: "smooth" });
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  return (
    <div className="page">
      {/* Фон */}
      <div className="bg-haze" aria-hidden />
      <div className="bg-pattern" aria-hidden />

      {/* Хедер */}
      <header className="header">
        <div className="wrap nav">
          <a href="#top" className="logo">
            MIRA — Game Designer
          </a>

          {/* переключатель языка */}
          <div className="lang">
            <button
              className={`lang-btn ${lang === "ru" ? "on" : ""}`}
              onClick={() => setLang("ru")}
              aria-pressed={lang === "ru"}
              title="Русский"
            >
              РУС
            </button>
            <button
              className={`lang-btn ${lang === "en" ? "on" : ""}`}
              onClick={() => setLang("en")}
              aria-pressed={lang === "en"}
              title="English"
            >
              ENG
            </button>
          </div>

          <nav className="menu">
            <a href="#about">{t.nav.about}</a>
            <a href="#skills">{t.nav.skills}</a>
            <a href="#projects">{t.nav.projects}</a>
            <a href="#petprojects">{t.nav.pet}</a>
            <a href="#contact">{t.nav.contact}</a>
          </nav>
        </div>
      </header>

      <main className="main">
        {/* HERO */}
        <section id="top" className="wrap hero">
          <div className="pill">{t.hero.badge}</div>
          <h1 className="h1">{t.hero.title}</h1>
          <p className="lead">{t.hero.lead}</p>
        </section>

        {/* ОБО МНЕ */}
        <section id="about" className="wrap section grid-5">
          <div className="col-2 about-left">
            <img src="/mira-photo.jpg" alt="Mira" className="avatar" />
            <div className="card">
              <div>
                {t.about.card.name}: <b>{t.aboutCardValues.name}</b>
              </div>
              <div>
                {t.about.card.lvl}: <b>{t.aboutCardValues.lvl}</b>
              </div>
              <div>
                {t.about.card.class}: <b>{t.aboutCardValues.class}</b>
              </div>
              <div>
                {t.about.card.sub}: <b>{t.aboutCardValues.sub}</b>
              </div>
              <div>
                {t.about.card.exp}: <b>{t.aboutCardValues.exp}</b>
              </div>
            </div>
          </div>
          <div className="col-3">
            <h2 className="h2">⚝ {t.about.h2}</h2>
            <div className="underline" />
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
            <p>{t.about.p3}</p>
            <a
              className="cv-btn"
              href={
                lang === "ru"
                  ? "/gamedesigner_polikarova_cv_ru.pdf"
                  : "/gamedesigner_polikarova_cv_eng.pdf"
              }
              download
            >
              {lang === "ru" ? "Скачать резюме" : "Download CV"}
            </a>
          </div>
        </section>

        {/* ПЕРКИ */}
        <section id="skills" className="wrap section">
          <h2 className="h2">✥ {t.skills.h2}</h2>
          <div className="underline" />
          <div className="cards3">
            {[t.skills.active, t.skills.passive, t.skills.inv].map((blk, i) => (
              <div className="card card-dark" key={i}>
                <div className="kicker">☽ {blk.title}</div>
                <ul>
                  {blk.items.map((s, j) => (
                    <li key={j}>{s}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* РАБОЧИЕ ПРОЕКТЫ */}
        <section id="projects" className="wrap section">
          <h2 className="h2">☥ {t.projects.h2}</h2>
          <div className="underline" />
          {t.projects.list.map((p, i) => (
            <div className={`proj ${i % 2 ? "rev" : ""}`} key={i}>
              <div className="proj-media">
                {p.video ? (
                  <div className="proj-video-frame">
                    <iframe
                      src={p.video}
                      title={p.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <img
                    src={p.image}
                    alt={p.title}
                    onClick={() => setZoomImage(p.image)}
                    className="zoomable"
                  />
                )}
              </div>

              <div className="proj-text">
                {/* водяной знак с компанией */}
                <div className="company-mark" aria-hidden>
                  {p.company}
                </div>

                {/* заголовок */}
                {p.link ? (
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="proj-title-link"
                  >
                    <h3 className="h3 proj-title">
                      {p.title}
                      <span className="link-icon" aria-hidden>
                        🔗
                      </span>
                    </h3>
                  </a>
                ) : (
                  <h3 className="h3 proj-title">{p.title}</h3>
                )}

                {/* короткое описание игры — сразу после заголовка */}
                <p className="proj-desc">{p.gameDesc}</p>

                {/* мета-чипы: должность + период */}
                <div className="proj-chips">
                  <span className="chip">{p.position}</span>
                  <span className="chip">{p.period}</span>
                </div>

                {/* обязанности */}
                <ul className="proj-tasks">
                  {Array.isArray(p.responsibilities) &&
                    p.responsibilities.map((r, idx) => <li key={idx}>{r}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </section>

        {/* ПЕТ-ПРОЕКТЫ */}
        <section id="petprojects" className="wrap section">
          <h2 className="h2">☿ {t.pet.h2}</h2>
          <div className="underline" />

          <div className="pet-grid">
            {t.pet.list.map((p, i) => (
              <a
                key={i}
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="pet-card"
              >
                <div className="pet-media">
                  <img src={p.image} alt={p.title} />
                  <div className="pet-overlay">
                    <div className="pet-text">
                      <h3>{p.title}</h3>
                      <p>{p.desc}</p>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* КОНТАКТЫ */}
        <section id="contact" className="wrap section grid-5">
          <div className="col-2">
            <h2 className="h2">❈ {t.contact.h2}</h2>
            <div className="underline" />
          </div>
          <div className="col-3 contacts">
            <a className="contact" href="mailto:hello@example.com">
              <div className="contact-title">{t.contact.email}</div>
              <div className="contact-sub">shadowofpain@mail.ru</div>
            </a>
            <div className="contact">
              <div className="contact-title">{t.contact.location}</div>
              <div className="contact-sub">{t.contact.remote}</div>
            </div>
            <a
              className="contact"
              href="https://www.linkedin.com/in/anastasiia-polikarova"
            >
              <div className="contact-title">LinkedIn</div>
              <div className="contact-sub">
                linkedin.com/in/anastasiia-polikarova
              </div>
            </a>
            <a className="contact">
              <div className="contact-title">Telegram</div>
              <div className="contact-sub">@Mirelle_Lioncourt</div>
            </a>
          </div>
        </section>

        {zoomImage && (
          <div className="zoom-overlay" onClick={closeZoom}>
            <img src={zoomImage} alt="zoomed" className="zoomed-img" />
          </div>
        )}
      </main>

      {/* Футер */}
      <footer className="footer">
        <div className="wrap footer-in">
          <div>© {new Date().getFullYear()} Mira — Game Design</div>
          <div className="phone">+382 68 533 615</div>
        </div>
      </footer>
    </div>
  );
}
